import React, { useState, useRef, useEffect } from 'react';
import { Box, Skeleton } from '@mui/material';

const OptimizedImage = ({
  src,
  alt,
  width,
  height,
  sx = {},
  loading = 'lazy',
  priority = false,
  placeholder = true,
  fallback = '/Logos/logoFocus.png',
  ...props
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageSrc, setImageSrc] = useState(priority ? src : null);
  const imgRef = useRef(null);
  const observerRef = useRef(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (priority || imageSrc) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setImageSrc(src);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: '50px', // Start loading 50px before the image enters viewport
        threshold: 0.1
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
      observerRef.current = observer;
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [src, priority, imageSrc]);

  const handleImageLoad = () => {
    setImageLoaded(true);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(false);
    // Try fallback image
    if (imageSrc !== fallback) {
      setImageSrc(fallback);
    }
  };

  // Preload critical images
  useEffect(() => {
    if (priority && src) {
      const img = new Image();
      img.onload = () => setImageLoaded(true);
      img.onerror = () => setImageError(true);
      img.src = src;
    }
  }, [priority, src]);

  const imageStyle = {
    width: width || '100%',
    height: height || 'auto',
    objectFit: 'cover',
    transition: 'opacity 0.3s ease-in-out',
    opacity: imageLoaded ? 1 : 0,
    ...sx
  };

  return (
    <Box
      ref={imgRef}
      sx={{
        position: 'relative',
        width: width || '100%',
        height: height || 'auto',
        overflow: 'hidden',
        ...sx
      }}
      {...props}
    >
      {/* Skeleton placeholder */}
      {placeholder && !imageLoaded && !imageError && (
        <Skeleton
          variant="rectangular"
          width={width || '100%'}
          height={height || 200}
          sx={{
            position: imageSrc ? 'absolute' : 'static',
            top: 0,
            left: 0,
            zIndex: 1
          }}
        />
      )}

      {/* Actual image */}
      {imageSrc && (
        <img
          src={imageSrc}
          alt={alt}
          loading={priority ? 'eager' : loading}
          onLoad={handleImageLoad}
          onError={handleImageError}
          style={imageStyle}
          decoding="async"
          // Add responsive image attributes
          sizes={width ? `${width}px` : '100vw'}
        />
      )}

      {/* Error state */}
      {imageError && imageSrc === fallback && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: height || 200,
            backgroundColor: 'grey.100',
            color: 'grey.500',
            fontSize: '0.875rem'
          }}
        >
          Image non disponible
        </Box>
      )}
    </Box>
  );
};

export default OptimizedImage;